import { GridDataEntry } from 'controls/grid/gridDataEntry';
import { GridStore } from 'controls/grid/gridStore';
import { GridState } from 'controls/grid/gridState';
import { getSettings, saveSettings } from 'framework/userSettings';
import { copyViaSerializr } from 'framework/serializr-integration';
import { deserialize, serialize } from 'serializr';
import { runInAction, toJS } from 'mobx';
import { iterateRules, RuleDefinitionType } from 'controls/queryBuilder/ruleDefinition';

export interface GridStateProvider<DataItem extends GridDataEntry> {
	getState(store: GridStore<DataItem>): Promise<GridState<DataItem>>;
	saveState(state: GridState<DataItem>, store: GridStore<DataItem>): Promise<void>;
}

type UserSettingsStateProviderProps<DataEntry extends GridDataEntry> = {
	category: string;

	//in case a field gets renamed
	mappings?: Record<string, string>;
};

export class UserSettingsStateProvider<DataEntry extends GridDataEntry> implements GridStateProvider<DataEntry> {
	props: UserSettingsStateProviderProps<DataEntry>;

	constructor(props: UserSettingsStateProviderProps<DataEntry>) {
		this.props = props;
	}

	async getState(store: GridStore<DataEntry>) {
		if (!this.props.category) return null;

		const { data: state } = await getSettings<GridState<DataEntry>>('grid-settings-' + this.props.category, {
			clazz: GridState,
			trackable: false,
			ignoreCache: true,
		});

		//getSettings always deserializes to a real (if empty) GridState instance, even for a category that was
		//never saved - treat an empty one as "nothing saved" so the caller falls back to config.defaults
		if (state.views.length === 0) return null;

		if (state.currentView != null) {
			this.updateLegacyFields(state);
		}

		return state;
	}

	updateLegacyFields(state: GridState<DataEntry>) {
		if (!this.props.mappings) return;

		for (const oldName of Object.keys(this.props.mappings)) {
			const newName = this.props.mappings[oldName];

			for (const view of state.views) {
				for (let i = 0; i < state.groupBy.length; i++) {
					if (view.groupBy[i] == oldName) {
						view.groupBy[i] = newName as keyof DataEntry;
					}
				}

				for (let i = 0; i < state.sortingOrder.length; i++) {
					if (view.sortingOrder[i] == oldName) {
						view.sortingOrder[i] = newName;
					}
				}

				for (const column of view.columns) {
					if (column.field == oldName) {
						column.field = newName;
					}
				}

				for (const rule of iterateRules(view.filters)) {
					if (rule.type != RuleDefinitionType.Rule) continue;

					if (rule.properties.field == oldName) {
						rule.properties.field = newName;
					}
				}
			}
		}
	}

	async saveState(state: GridState<DataEntry>, store: GridStore<DataEntry>) {
		await saveSettings('grid-settings-' + this.props.category, serialize(GridState<DataEntry>, state));
	}
}

export class ExternalStateProvider<DataItem extends GridDataEntry> implements GridStateProvider<DataItem> {
	state: GridState<DataItem>;

	constructor(state: GridState<DataItem>) {
		this.state = state;
	}

	async getState(store: GridStore<DataItem>) {
		if (!this.state) return null;

		return copyViaSerializr(this.state);
	}

	async saveState(state: GridState<DataItem>, store: GridStore<DataItem>) {
		this.state = copyViaSerializr(state);
	}
}

// Kept serialized in an object of the grid's owner, which saves it along with its own data.
export class ObjectStateProvider<DataItem extends GridDataEntry> implements GridStateProvider<DataItem> {
	constructor(
		private holder: Record<string, unknown>,
		private key: string,
	) {}

	getState() {
		const saved = this.holder[this.key];
		return Promise.resolve(saved == null ? null : deserialize<GridState<DataItem>>(GridState, toJS(saved)));
	}

	saveState(state: GridState<DataItem>) {
		runInAction(() => {
			this.holder[this.key] = serialize(GridState<DataItem>, state);
		});
		return Promise.resolve();
	}
}
